import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { OnboardingFlow } from '../components/onboarding/onboarding-flow';
import { OnboardingData } from '../components/onboarding/steps/completion-step';
import { memberService } from '../services';
import { dateToISOString, inputValueToDate, dateToInputValue } from '../utils/formatters';

const NewOnboarding: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Mark onboarding as started immediately when component mounts
    // Only set to false if it's undefined (never started)
    // Don't change it if it's already true (completed/skipped)
    const markOnboardingStarted = async () => {
      if (user && user.onboardingCompleted === undefined) {
        try {
          await memberService.updateProfile({ onboardingCompleted: false });
          await refreshUser();
        } catch (error) {
          console.error('Failed to mark onboarding as started:', error);
        }
      }
    };

    markOnboardingStarted();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleComplete = async (data: OnboardingData) => {
    if (!user) return;

    try {
      // Upload avatar if provided (this updates photoURL automatically)
      if (data.avatar) {
        await memberService.uploadOwnPhoto(data.avatar);
      }

      // Update user profile with onboarding data
      // Use dateToInputValue to get YYYY-MM-DD, then dateToISOString for noon UTC
      await memberService.updateProfile({
        birthDate: data.birthday ? dateToISOString(dateToInputValue(data.birthday)) : undefined,
        professionalDescription: data.professionalDescription,
        professionalHabit: data.professionalHabit,
        friendGroupKnownFor: data.friendGroupKnownFor,
        hobbies: data.hobbies,
        lifeGoal: data.lifeGoal,
        funFact: data.funFact,
        careerLearning: data.careerLearning,
        onboardingCompleted: true,
      });

      // Refresh user to get complete data including photoURL from photo upload
      await refreshUser();

      // Navigate to dashboard
      navigate('/dashboard');
    } catch (error: any) {
      console.error('Failed to complete onboarding:', error);
      alert(error.response?.data?.message || 'Failed to save onboarding data. Please try again.');
    }
  };

  const handleSkip = async () => {
    if (!user) return;

    try {
      // Mark onboarding as completed even if skipped
      await memberService.updateProfile({ onboardingCompleted: true });

      // Refresh user to ensure we have complete data
      await refreshUser();

      navigate('/dashboard');
    } catch (error) {
      console.error('Failed to skip onboarding:', error);
      navigate('/dashboard');
    }
  };

  // Prepare initial data from existing user profile
  // Use inputValueToDate to create a Date at noon UTC, avoiding timezone issues
  const initialData: Partial<OnboardingData> = {
    birthday: user?.birthDate ? inputValueToDate(dateToInputValue(user.birthDate)) : null,
    professionalDescription: user?.professionalDescription || '',
    professionalHabit: user?.professionalHabit || '',
    friendGroupKnownFor: user?.friendGroupKnownFor || '',
    hobbies: user?.hobbies || '',
    lifeGoal: user?.lifeGoal || '',
    funFact: user?.funFact || '',
    careerLearning: user?.careerLearning || '',
  };

  return (
    <main className="min-h-screen bg-background" data-testid="new-onboarding-page">
      <OnboardingFlow 
        onComplete={handleComplete} 
        onSkip={handleSkip}
        initialData={initialData}
        existingPhotoURL={user?.profilePictureUrl || user?.photoURL}
      />
    </main>
  );
};

export default NewOnboarding;
